import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { invalidateAfterMutation } from "@/core/api/invalidate-after-mutation";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type { QrCodeDto } from "@/features/merchant/api/dto";

/**
 * Révoque un QR code
 * (`POST /QrCodes/{id}/revoke?actingMerchantId=...&reason=...`).
 * Rôles : merchant avec permission `REVOKE_QR_CODE` ou back-office.
 */
export const useRevokeQrCode = (merchantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "qr-codes", "revoke", merchantId],
    mutationFn: ({
      qrCodeId,
      actingMerchantId,
      reason,
    }: {
      qrCodeId: string;
      actingMerchantId?: string;
      reason: string;
    }) => {
      const params = new URLSearchParams({ reason });
      if (actingMerchantId) params.set("actingMerchantId", actingMerchantId);
      return mutateData({
        url: `${merchantEndpoints.qrCodes.revoke(qrCodeId)}?${params.toString()}`,
        method: "POST",
      }) as Promise<QrCodeDto>;
    },
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, [
        QUERY_KEYS.MERCHANT_QR_CODES_BY_MERCHANT(merchantId),
        ["MERCHANT_QR_CODE_BY_TOKEN"],
      ]);
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useRevokeQrCode failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
